import { RiArrowRightDoubleLine } from "@remixicon/react";
import React, {
    ReactElement, useEffect, useMemo, useState,
} from "react";
import { FiTrash2 } from "react-icons/fi";
import {
    Comparison,
    ConversationSourceType,
    FormFieldReplacementInput, FormFieldType, WorkflowConditionMapping,
} from "../../../__generated__/api";
import EditableControl, { ControlType } from "../../editable-text/EditableText";
import ExpandingSection from "../../expanding-section/ExpandingSection";
import ConversationSources from "../../pickers/conversation-sources/ConversationSources";
import { ActionProps } from "./base";
import Select from "../../select/Select";
import { useTheme } from "../../../contexts/Theme";
import Styles from "./map-channel-messages.module.scss";
import Button from "../../theme/button/Button";
import AdvancedSection from "../../expanding-section/AdvancedSection";

export const TICKET_SOURCE_GROUP = "urn:fq:form-field-group:source";

export const TICKET_SOURCE_ID = "urn:fq:form-field:source-id";

const MAPPING_FIELD = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,mapping)";
const MESSAGE_FIELD = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,message)";
const THREAD_RESPONSE_FIELD = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,threadResponse)";
const POSTING_USER_FIELD = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,postingUser)";

enum PostingUser {
    REQUESTER = "REQUESTER",
    AGENT = "AGENT"
}

const POSTING_TITLE = {
    [PostingUser.REQUESTER]: "Requester",
    [PostingUser.AGENT]: "Agent",
};

export default function MapChannelSettings(props: ActionProps): ReactElement {
    const theme = useTheme();

    const [lastValue, setValue] = useState<string>();
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const mappingFieldParam = props.values.find(row => row.value.fieldId === MAPPING_FIELD);
    const messageParam = props.values.find(row => row.value.fieldId === MESSAGE_FIELD)?.value?.string;
    const threadResponseParam = props.values.find(row => row.value.fieldId === THREAD_RESPONSE_FIELD)?.value?.string;
    const postingUserParam = props.values.find(row => row.value.fieldId === POSTING_USER_FIELD)?.value?.string;

    const [message, setMessage] = useState<string | undefined>(messageParam as string);
    const [threadResponse, setThreadResponse] = useState<string | undefined>(threadResponseParam as string);
    const [postingUser, setPostingUser] = useState<string | undefined>(postingUserParam as string ?? PostingUser.REQUESTER);

    const [replacements, setReplacements] = useState<FormFieldReplacementInput[]>(props.values[0]?.replacement ?? []);

    const [mapGroups, setMapGroups] = useState<WorkflowConditionMapping>(() => ({
        mapping: [
            {
                conditions: [
                    {
                        comparison: Comparison.Equal,
                        fieldId: "",
                    },
                ],
                value: {
                    fieldId: "",
                    type: FormFieldType.Text,
                },
            },
        ],
        valueType: FormFieldType.Text,
    }));

    useEffect(() => {
        const serialized = JSON.stringify(mapGroups);
        setValue(serialized);

        if (!isDirty) {
            return;
        }
        props.onChange({
            values: [
                {
                    fieldId: MAPPING_FIELD,
                    type: FormFieldType.LongText,
                    displayValue: "Channel Mapping",
                    string: serialized,
                },
                {
                    fieldId: MESSAGE_FIELD,
                    type: FormFieldType.LongText,
                    displayValue: message,
                    string: message,
                },
                {
                    fieldId: THREAD_RESPONSE_FIELD,
                    type: FormFieldType.LongText,
                    displayValue: threadResponse,
                    string: threadResponse,
                },
                {
                    fieldId: POSTING_USER_FIELD,
                    type: FormFieldType.LongText,
                    displayValue: "Posting User",
                    string: postingUser,
                },
            ],
            replacements,
            output: [],
        });
    }, [mapGroups, message, threadResponse, postingUser, replacements]);

    useEffect(() => {
        if (props.values?.[0]?.value?.string && lastValue !== props.values[0].value.string) {
            setMapGroups(JSON.parse(props.values[0].value?.string));
        }
    }, [mappingFieldParam?.value?.string]);

    useEffect(() => {
        setMessage(messageParam ?? "");
    }, [messageParam]);

    useEffect(() => {
        setThreadResponse(threadResponseParam ?? "");
    }, [threadResponseParam]);

    useEffect(() => {
        setPostingUser(postingUserParam ?? PostingUser.REQUESTER);
    }, [postingUserParam]);

    const addGroup = () => {
        const mapping = Array.from(mapGroups.mapping);
        mapping.push({
            conditions: [
                {
                    comparison: Comparison.Equal,
                    fieldId: "",
                },
            ],
            value: {
                fieldId: "",
                type: FormFieldType.Text,
            },
        });
        setIsDirty(true);
        setMapGroups({
            mapping,
            valueType: mapGroups.valueType,
        });
    };

    const deleteCondition = (groupIndex: number) => {
        const mapping = Array.from(mapGroups.mapping);
        mapping.splice(groupIndex, 1);
        setIsDirty(true);
        setMapGroups({
            mapping,
            valueType: mapGroups.valueType,
        });
    };

    const advancedSettings = useMemo(() => (
        <>
            <div className="mb-4">
                <div className="">
                    Message
                </div>
                <div className="">
                    <EditableControl
                        type={ControlType.LONG_TEXT}
                        value={message}
                        isEditing
                        isRequired={false}
                        placeholder="A new message..."
                        inputClassName="form-control"
                        onChange={(text, replace) => {
                            setMessage(text as string);
                            setIsDirty(true);
                            setReplacements([
                                ...replacements,
                                ...(replace ?? []),
                            ].dedupe(row => row.groupId + row.fieldId));
                        }}
                        isFormField
                        replaceableInput={props.input}
                        replacements={replacements}
                        shouldUseEditor={theme.isV3}
                    />
                </div>
                <small>
                    What is the message that should be sent. By default this will be the description.
                </small>
            </div>

            <div className="mb-4">
                <div className="">
                    Thread Response
                </div>
                <div className="">
                    <EditableControl
                        type={ControlType.LONG_TEXT}
                        value={threadResponse}
                        isEditing
                        isRequired={false}
                        placeholder="Please use the following thread..."
                        inputClassName="form-control"
                        onChange={(text, replace) => {
                            setThreadResponse(text as string);
                            setIsDirty(true);
                            setReplacements([
                                ...replacements,
                                ...(replace ?? []),
                            ].dedupe(row => row.groupId + row.fieldId));
                        }}
                        isFormField
                        replaceableInput={props.input}
                        replacements={replacements}
                        shouldUseEditor={theme.isV3}
                    />
                </div>
                <small>
                    What should be the message for the start of the thread?
                </small>
            </div>

            <div className="mb-4">
                <div className="">
                    Post as
                </div>
                <div className="">
                    <Select
                        onChange={value => {
                            setPostingUser(value.value);
                            setIsDirty(true);
                        }}
                        value={postingUser ? {
                            label: POSTING_TITLE[postingUser as PostingUser ?? PostingUser.REQUESTER],
                            value: postingUser,
                        } : undefined}
                        options={[
                            {
                                label: POSTING_TITLE[PostingUser.REQUESTER],
                                value: PostingUser.REQUESTER,
                            },
                            {
                                label: POSTING_TITLE[PostingUser.AGENT],
                                value: PostingUser.AGENT,
                            },
                        ]}
                    />
                </div>
                <small>
                    Who should the user be sent as?
                </small>
            </div>
        </>
    ), [message, threadResponse, postingUser, replacements, theme.isV3]);

    return (
        <div className="list-group list-flush">

            {mapGroups.mapping.map((conditionGroup, groupIndex) => {
                const condition = conditionGroup.conditions.firstElement;
                const conditionValue = condition?.value?.string as string;

                return (
                    <div
                        className={theme.isV3
                            ? Styles["group-mapping"] : "list-group-item bg-gray-200 mb-3 d-flex justify-content-between align-items-center"}
                        // eslint-disable-next-line react/no-array-index-key
                        key={`orGroup-${groupIndex}`}
                    >

                        <div className="col">
                            <ConversationSources
                                onSelected={(value, name) => {
                                    const mapping = Array.from(mapGroups.mapping);
                                    if (value) {
                                        mapping[groupIndex].conditions[0] ??= {};
                                        mapping[groupIndex].conditions[0].fieldId = TICKET_SOURCE_ID;
                                        mapping[groupIndex].conditions[0].stepId = TICKET_SOURCE_GROUP;
                                        mapping[groupIndex].conditions[0].value = {
                                            fieldId: TICKET_SOURCE_ID,
                                            string: value,
                                            type: FormFieldType.AsyncDropdown,
                                            displayValue: name,
                                        };
                                    }
                                    setIsDirty(true);
                                    setMapGroups({
                                        mapping,
                                        valueType: mapGroups.valueType,
                                    });
                                }}
                                value={conditionValue}
                                sourceTypes={[ConversationSourceType.SlackChannel, ConversationSourceType.MicrosoftTeams]}
                            />
                        </div>
                        <div className="col-2 px-3">

                            Connects to
                            {" "}
                            <RiArrowRightDoubleLine />
                        </div>

                        <div className="col">
                            <ConversationSources
                                onSelected={(value, name) => {
                                    const mapping = Array.from(mapGroups.mapping);
                                    if (value) {
                                        mapping[groupIndex].value = {
                                            fieldId: TICKET_SOURCE_ID,
                                            string: value,
                                            type: FormFieldType.AsyncDropdown,
                                            displayValue: name,
                                        };
                                    }
                                    setIsDirty(true);
                                    setMapGroups({
                                        mapping,
                                        valueType: mapGroups.valueType,
                                    });
                                }}
                                value={conditionGroup.value?.string as string}
                                sourceTypes={[ConversationSourceType.SlackChannel, ConversationSourceType.MicrosoftTeams]}
                            />
                        </div>
                        <div className="col-2 d-flex justify-content-end">
                            {groupIndex > 0 && (
                                <button
                                    aria-label="Remove Condition"
                                    type="button"
                                    className="btn btn-none"
                                    onClick={() => deleteCondition(groupIndex)}
                                >
                                    <FiTrash2 />
                                </button>
                            )}

                        </div>

                    </div>
                );
            })}
            <div className="mb-3">

                {theme.isV3 ? (
                    <Button.Add variant="outline-blue" onClick={addGroup}>
                        Add Mapping
                    </Button.Add>
                ) : (
                    <button type="button" className="btn btn-outline mt-2" onClick={addGroup}>
                        Add Mapping
                    </button>
                )}

            </div>
            {
                theme.isV3 ? (
                    <AdvancedSection title="Advanced Settings">
                        {advancedSettings}
                    </AdvancedSection>
                ) : (
                    <ExpandingSection title="Advanced">
                        {advancedSettings}
                    </ExpandingSection>
                )
            }
        </div>
    );
}
